import React, { JSX, useEffect, useMemo } from "react";

import { useTheme } from "@mui/material";
import dayjs from "dayjs";
import { type EChartsOption } from "echarts-for-react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { axisDateFormatter } from "src/components/Dashboard/helpers/dashboardChart.helper";
import { ChartTooltipFormatterParams } from "src/components/Dashboard/interfaces/DashboardComponentParams";
import { type IntervalTab } from "src/components/IntervalTabs/enum/IntervalTab";
import { chartTooltipFormatter } from "src/components/Tooltip/helpers/chartTooltipFormatter";
import SeriesType from "src/constants/seriesType.enum";
import getSeriesColor from "src/lib/color.helper";
import { adjustCurrentValues, getIntervalFromSelectedTab, getMaxDate, getMinDate } from "src/lib/date.helper";
import { getCountDateTotalCount, getCountDateTotalPastCount } from "src/lib/number.helpers";
import {
  type MeasuresItemizedCompactItemDto,
  type MeasureItemDto,
  type MeasureItem,
  MeasuresWithHasData,
} from "src/types/dashboardDatasetDto";

const ReactECharts = React.lazy(() => import("echarts-for-react"));

const processValues = (
  map: Map<string, Map<number, MeasureItem[]>>,
  itemName: string,
  values?: MeasureItemDto[],
  valuesPast?: MeasureItemDto[]
): void => {
  if (!values) return;

  const sortedValues = values.sort((a, b) => (a.measureTime?.valueOf() ?? 0) - (b.measureTime?.valueOf() ?? 0));
  const sortedValuesPast = (valuesPast as unknown as MeasureItemDto[]).sort(
    (a, b) => (a.measureTime?.valueOf() ?? 0) - (b.measureTime?.valueOf() ?? 0)
  );

  const performerMap = map.get(itemName) ?? new Map<number, MeasureItem[]>();
  map.set(itemName, performerMap);

  for (const [i, value] of sortedValues.entries()) {
    const date = value?.measureTime?.valueOf();

    const pastValue = sortedValuesPast[i];

    const items = performerMap.get(date) ?? [];
    items.push({
      ...value,
      measureTimePast: pastValue?.measureTime ?? null,
      countPast: pastValue?.count ?? 0,
    });
    performerMap.set(date, items);
  }
};

function FootfallPinnedLocationsChart({
  selectedTab,
  chartData,
}: {
  selectedTab: IntervalTab;
  chartData: MeasuresItemizedCompactItemDto[];
}): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();

  const subLocationsData = useMemo(() => {
    if (!chartData) return [];
    return chartData.map((item) => adjustCurrentValues(item));
  }, [chartData]);

  const allDates = useMemo(
    () => subLocationsData.flatMap((item) => item.currentValues?.map((value: MeasureItemDto) => value.measureTime) ?? []),
    [subLocationsData]
  );

  const minDate = useMemo(() => getMinDate(allDates), [allDates]);
  const maxDate = useMemo(() => getMaxDate(allDates), [allDates]);

  const createDataMap = (): Map<string, Map<number, MeasureItem[]>> => {
    const map = new Map<string, Map<number, MeasureItem[]>>();

    for (const subLocationItem of subLocationsData) {
      const itemName = subLocationItem.locationName ?? "";
      processValues(map, itemName, subLocationItem?.currentValues ?? [], subLocationItem?.pastValues ?? []);
    }
    return map;
  };
  const timePerformersDataMap = createDataMap();

  const seriesData = useMemo(() => {
    const sortedPerformers = [...timePerformersDataMap.entries()]
      .map(([performer, performerItems]) => {
        let totalValue = 0;
        for (const [, measureItemDtos] of performerItems.entries()) {
          const currentValue = getCountDateTotalCount(measureItemDtos);
          totalValue += currentValue;
        }

        return { performer, totalValue, performerItems };
      })
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 5);

    return sortedPerformers.map(({ performer, performerItems }, index) => {
      const data = [...performerItems.entries()]
        .sort(([timeIntA], [timeIntB]) => timeIntA - timeIntB)
        .map(([timeInt, measureItemDtos]) => {
          const currentValue = getCountDateTotalCount(measureItemDtos);
          const pastValue = getCountDateTotalPastCount(measureItemDtos);
          const measureItem = measureItemDtos[0] as MeasuresWithHasData<MeasureItem>;

          return [timeInt, currentValue, measureItemDtos[0]?.measureTimePast ?? null, pastValue, measureItem.hasData];
        });

      return {
        name: performer,
        type: SeriesType.Line,
        emphasis: { focus: "series" },
        showSymbol: false,
        data,
        itemStyle: { color: getSeriesColor(index, theme) },
      };
    });
  }, [timePerformersDataMap, theme]);

  const option: Partial<EChartsOption> = useMemo(
    () => ({
      title: { show: false },
      tooltip: {
        trigger: "axis",
        confine: true,
        formatter: (params: ChartTooltipFormatterParams) => chartTooltipFormatter(params, t, theme, null, true),
        axisPointer: {
          type: "shadow",
        },
        borderColor: theme.palette.border.default,
      },
      grid: {
        top: 30,
        left: 3,
        right: "2%",
        bottom: 0,
        containLabel: true,
      },
      calculable: true,
      xAxis: [
        {
          type: "category",
          boundaryGap: false,
          axisLabel: {
            formatter: (value: string) => {
              const date = dayjs(Number(value));
              const intervalType = getIntervalFromSelectedTab(selectedTab);
              return axisDateFormatter(intervalType, date, t);
            },
          },
        },
      ],
      yAxis: [{ type: "value" }],
      series: seriesData,
    }),
    [seriesData, selectedTab, theme, t]
  );

  useEffect(() => {
    ReactGA.event("feature_usage", {
      feature: "footfall_pinned_malls_chart",
      tab: selectedTab,
      seriesCount: seriesData.length,
    });
  }, [selectedTab, seriesData.length]);

  return <ReactECharts option={option} key={`${minDate.valueOf()}-${maxDate.valueOf()}-${seriesData.length}`} />;
}

export default FootfallPinnedLocationsChart;
